import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import axios from 'axios';
import { 
  School, 
  Users, 
  Calendar,
  RefreshCw,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MapPin,
  DollarSign,
  TrendingUp,
  Building2
} from 'lucide-react';

const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTerm, setActiveTerm] = useState(null);
  const [districtStats, setDistrictStats] = useState(null);
  const [districtStatsLoading, setDistrictStatsLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSubscriptions();
    fetchActiveTerm();
    fetchDistrictStats();
  }, []);

  useEffect(() => {
    filterSubscriptions();
  }, [subscriptions, searchTerm, statusFilter]);

  const fetchDistrictStats = async () => {
    try {
      setDistrictStatsLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/subscriptions/district-stats`, {
        withCredentials: true
      });
      setDistrictStats(response.data);
    } catch (error) {
      console.error('Error fetching district stats:', error);
      toast.error("Failed to fetch district statistics.");
    } finally {
      setDistrictStatsLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/subscriptions`, {
        withCredentials: true
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error("Failed to fetch subscriptions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTerm = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/terms`, {
        withCredentials: true
      });
      const activeTermData = response.data.find(term => term.is_active);
      setActiveTerm(activeTermData);
    } catch (error) {
      console.error('Error fetching active term:', error);
    }
  };

  const filterSubscriptions = () => {
    let filtered = subscriptions;

    // Filter by search term (teacher name or email)
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.teacher_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.teacher_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    setFilteredSubscriptions(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-600', icon: XCircle, label: 'Inactive' },
      expired: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Expired' },
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Unknown' };
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getSubscriptionStats = () => {
    const total = subscriptions.length;
    const active = subscriptions.filter(sub => sub.status === 'active').length;
    const inactive = subscriptions.filter(sub => sub.status === 'inactive').length;
    const expired = subscriptions.filter(sub => sub.status === 'expired').length;

    return { total, active, inactive, expired };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <School className="h-5 w-5" />
            Subscription Management
          </CardTitle>
          <CardDescription>
            View and manage teacher subscriptions and term linkages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading subscriptions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats = getSubscriptionStats();

  return (
    <div className="space-y-6">
      {/* Statistics Card - Only Total Subscriptions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
          <p className="text-xs text-muted-foreground">All teacher subscriptions</p>
        </CardContent>
      </Card>

      {/* District Revenue Stats Section */}
      {districtStatsLoading ? (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading district statistics...</span>
            </div>
          </CardContent>
        </Card>
      ) : districtStats && (
        <>
          {/* Revenue Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-emerald-800">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-700">
                  GH₵ {((districtStats.summary.total_subscriptions || 0) * (districtStats.summary.subscription_price || 0)).toLocaleString()}
                </div>
                <p className="text-xs text-emerald-600">Total Subs × Amount/Sub</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-blue-800">Districts Covered</CardTitle>
                <Building2 className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-700">
                  {districtStats.summary.total_districts || 0}
                </div>
                <p className="text-xs text-blue-600">With active subscriptions</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-purple-800">Amount/Subscription</CardTitle>
                <TrendingUp className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-700">
                  GH₵ {districtStats.summary.subscription_price?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-purple-600">Per subscription</p>
              </CardContent>
            </Card>
          </div>

          {/* District Breakdown Table */}
          {districtStats.districts && districtStats.districts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  Revenue by District
                </CardTitle>
                <CardDescription>
                  Subscription statistics per district (created by Directors)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>District</TableHead>
                      <TableHead className="text-center">Total Subs</TableHead>
                      <TableHead className="text-center">Amount/Sub (GH₵)</TableHead>
                      <TableHead className="text-right">Revenue (GH₵)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {districtStats.districts
                      .sort((a, b) => (b.total_subscriptions * (districtStats.summary.subscription_price || 0)) - (a.total_subscriptions * (districtStats.summary.subscription_price || 0)))
                      .map((district, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              {district.district_name}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{district.total_subscriptions}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-purple-100 text-purple-800">{districtStats.summary.subscription_price?.toLocaleString() || '0'}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            {(district.total_subscriptions * (districtStats.summary.subscription_price || 0)).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    
                    {/* Private Schools row */}
                    {districtStats.summary.private_schools_subscriptions > 0 && (
                      <TableRow className="bg-amber-50/50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span className="text-amber-700">Private Schools</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-amber-300">
                            {districtStats.summary.private_schools_subscriptions}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-purple-100 text-purple-800">{districtStats.summary.subscription_price?.toLocaleString() || '0'}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-amber-600">
                          {(districtStats.summary.private_schools_subscriptions * (districtStats.summary.subscription_price || 0)).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Total row */}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-center">{districtStats.summary.total_subscriptions}</TableCell>
                      <TableCell className="text-center">-</TableCell>
                      <TableCell className="text-right text-emerald-700">
                        {(districtStats.summary.total_subscriptions * (districtStats.summary.subscription_price || 0)).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* No districts message */}
          {(!districtStats.districts || districtStats.districts.length === 0) && (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No district data available</p>
                  <p className="text-sm mt-1">
                    Districts are created when SISOs register with district information
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Active Term Alert */}
      {activeTerm && (
        <Alert className="border-blue-200 bg-blue-50">
          <Calendar className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>{activeTerm.name}</strong> is the currently active term. New subscriptions are automatically linked to this term.
          </AlertDescription>
        </Alert>
      )}

      {!activeTerm && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            No term is currently active. Teachers cannot subscribe until a term is activated.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Subscription Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <School className="h-5 w-5" />
            Teacher Subscriptions
          </CardTitle>
          <CardDescription>
            View and manage all teacher subscriptions and their term linkages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by teacher name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
              <Button 
                onClick={fetchSubscriptions} 
                variant="outline"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Subscriptions Table */}
          {filteredSubscriptions.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Teacher</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Term</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Subscribed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell className="font-medium">{subscription.teacher_name}</TableCell>
                      <TableCell className="text-gray-600">{subscription.teacher_email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Calendar className="h-3 w-3" />
                          {subscription.term_name}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(subscription.status)}</TableCell>
                      <TableCell className="text-gray-600">
                        {new Date(subscription.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions found</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No subscriptions match your current filters.' 
                  : 'No teacher subscriptions have been created yet.'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Management Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Subscription Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Automatic Term Linking</p>
                <p className="text-sm text-gray-600">New teacher subscriptions are automatically linked to the currently active academic term.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Paystack Integration</p>
                <p className="text-sm text-gray-600">All subscription payments are securely processed through Paystack payment gateway.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Subscription Validity</p>
                <p className="text-sm text-gray-600">Subscriptions remain valid for the entire duration of their linked term until the term is deactivated.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Status Management</p>
                <p className="text-sm text-gray-600">Subscription status automatically updates based on term activity and payment status.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManagement;